import _ from 'lodash';

import { AiChatPlanStatus, AiChatPlanStepStatus, AiPlanStepKind } from 'constants/aiChat';

const getStepIdentifier = step => step.stepKey ?? step.id ?? null;

// DEV-2913: SKIPPED + CANCELLED count as terminal for progress purposes.
// SKIPPED = LLM diverged from the plan. CANCELLED = user rejected the FE
// confirmation card. Both leave the row in a "done" state for the header
// counter; otherwise the card would stay stuck on "N of M done" forever.
const PLAN_STEP_TERMINAL_STATUSES = new Set([
  AiChatPlanStepStatus.COMPLETED,
  AiChatPlanStepStatus.FAILED,
  AiChatPlanStepStatus.SKIPPED,
  AiChatPlanStepStatus.CANCELLED,
]);

const PLAN_TERMINAL_STATUSES = new Set([
  AiChatPlanStatus.COMPLETED,
  AiChatPlanStatus.FAILED,
  AiChatPlanStatus.CANCELLED,
]);

const SYNTHESIS_STEP_KEY = 'synthesize';

// Matches planner-emitted "narrative" steps that have no tool to call and
// just describe how the model intends to summarize the gathered data
// (e.g. "Synthesize and present the borrower information"). The orchestrator
// already runs an automatic final-response pass after the executable steps,
// so these planner-narrative rows duplicate it as an unstyled blue band that
// never transitions out of "active" — pure clutter.
const SYNTHESIS_DESCRIPTION_PATTERN = /^\s*(synthesi[sz]e|summari[sz]e|present|compile|format)\b/i;

const hasToolName = step => _.isString(step.toolName) && step.toolName.length > 0;

const looksLikePlannerSynthesisNarrative = step => {
  if (hasToolName(step)) {
    return false;
  }

  if (!_.isString(step.description) || step.description.length === 0) {
    return false;
  }

  return SYNTHESIS_DESCRIPTION_PATTERN.test(step.description);
};

// The planner emits a MODEL_TURN "synthesize" step that represents the
// model's final-response pass. Its output is already streamed as the
// BotMessage body, so rendering it as a plan row is redundant clutter.
// `kind` only arrives once `plan_step_started` fires, so we also fall back
// to the well-known step identifier and to a description-pattern match so
// the row never flashes during the `plan_created` window before the BE has
// merged its synthesizer event.
export const isVisiblePlanStep = step => {
  if (step.kind === AiPlanStepKind.MODEL_TURN) {
    return false;
  }

  if (getStepIdentifier(step) === SYNTHESIS_STEP_KEY) {
    return false;
  }

  return !looksLikePlannerSynthesisNarrative(step);
};

// Counts of executable plan steps (everything except the synthesis MODEL_TURN
// placeholder, which is internal scaffolding rather than a user-visible step).
// Used by the plan card header to render "X of Y done" progress and by the
// auto-collapse heuristic to know when to fold the card.
export const getPlanProgressCounts = steps => {
  if (!Array.isArray(steps)) {
    return { done: 0, total: 0 };
  }

  const visibleSteps = steps.filter(isVisiblePlanStep);
  const done = visibleSteps.filter(step => PLAN_STEP_TERMINAL_STATUSES.has(step.status)).length;

  return { done, total: visibleSteps.length };
};

export const isPlanTerminal = planStatus => PLAN_TERMINAL_STATUSES.has(planStatus);

const PLAN_FAILED_STATUSES = new Set([AiChatPlanStatus.FAILED, AiChatPlanStatus.CANCELLED]);

export const resolvePlanHeaderIconVariant = planStatus => {
  if (planStatus === AiChatPlanStatus.IN_PROGRESS) {
    return 'active';
  }

  if (PLAN_FAILED_STATUSES.has(planStatus)) {
    return 'failed';
  }

  if (planStatus === AiChatPlanStatus.COMPLETED) {
    return 'done';
  }

  return 'active';
};

const ERROR_PATTERNS = [
  {
    test: message => /\b401\b|unauthor/i.test(message),
    friendly:
      'You are not signed in for this action. Try refreshing the page and signing in again.',
  },
  {
    test: message => /\b403\b|forbidden|permission/i.test(message),
    friendly:
      "I don't have permission to do this on your account. Please contact your administrator.",
  },
  {
    test: message => /\b404\b|not\s*found/i.test(message),
    friendly: "I couldn't find the requested record on this loan.",
  },
  {
    test: message => /timeout|timed\s*out/i.test(message),
    friendly: 'The request took too long to complete. Please try again.',
  },
  {
    test: message => /network|fetch|connection|offline/i.test(message),
    friendly: "I couldn't reach the server. Please check your connection and try again.",
  },
];

// Reduces a raw BE error string (which may include stack traces, exception
// types, or internal identifiers) to a single friendly sentence safe to show
// to a loan officer. Falls back to the first sentence when no pattern matches.
export const sanitizeStepErrorMessage = error => {
  if (!_.isString(error) || error.length === 0) {
    return 'Something went wrong. Please try again.';
  }

  const firstLine = error.split('\n')[0].trim();

  const pattern = ERROR_PATTERNS.find(({ test }) => test(firstLine));

  if (pattern) {
    return pattern.friendly;
  }

  const firstSentence = firstLine.split(/(?<=[.!?])\s/)[0];

  return firstSentence.length > 0 ? firstSentence : 'Something went wrong. Please try again.';
};

const getDependsOn = step => (Array.isArray(step.dependsOn) ? step.dependsOn : []);

export const groupStepsByDepth = steps => {
  if (!Array.isArray(steps) || steps.length === 0) {
    return [];
  }

  const stepIdentifiers = new Set(
    steps.map(getStepIdentifier).filter(identifier => !_.isNil(identifier)),
  );

  const depthMap = new Map();

  const computeDepth = (step, visiting) => {
    const id = getStepIdentifier(step);

    if (!_.isNil(id) && depthMap.has(id)) {
      return depthMap.get(id);
    }

    if (!_.isNil(id) && visiting.has(id)) {
      return 0;
    }

    if (!_.isNil(id)) {
      visiting.add(id);
    }

    const dependencies = getDependsOn(step).filter(depId => stepIdentifiers.has(depId));

    if (dependencies.length === 0) {
      const depth = 0;

      if (!_.isNil(id)) {
        depthMap.set(id, depth);
      }

      return depth;
    }

    const parentDepths = dependencies.map(depId => {
      const parent = steps.find(candidate => getStepIdentifier(candidate) === depId);

      if (_.isNil(parent)) {
        return -1;
      }

      return computeDepth(parent, visiting);
    });

    const depth = Math.max(...parentDepths) + 1;

    if (!_.isNil(id)) {
      depthMap.set(id, depth);
    }

    return depth;
  };

  const grouped = new Map();

  steps.forEach(step => {
    const depth = computeDepth(step, new Set());
    const bucket = grouped.get(depth) ?? [];

    bucket.push(step);
    grouped.set(depth, bucket);
  });

  return Array.from(grouped.entries())
    .sort(([depthA], [depthB]) => depthA - depthB)
    .map(([depth, depthSteps]) => ({ depth, steps: depthSteps }));
};
